import React, { useMemo } from "react";
import { EmptyStateErro } from "../../components/EmptyState";
import PainelTabela from "../../components/PainelTabela";
import { PageLoader } from "../../components/LoadingMarcenaria";
import { useDados } from "../../context/DadosContext";
import { addDiasUteis } from "../../utils/calculos";
import { aprovadoAntesDaRevisao, estaNaFilaDoPpcp } from "../../utils/fluxoExecutivo";
import { F } from "../../utils/gerencialFields";
import { buildDeadlineStatus, compareDatesAsc } from "../../utils/status";

const COLUNAS = [
  { key: "cliente", label: "Cliente", noWrap: true },
  { key: "ambiente", label: "Ambiente" },
  { key: "entregaContrato", label: "Entrega Prevista", labelCurto: "entrega", tipo: "data", width: "100px" },
  { key: "dataAprovacao", label: "Data Aprovação", labelCurto: "aprovação", tipo: "data", width: "100px" },
  { key: "limiteOtimizacao", label: "Limite Otimização", labelCurto: "limite otim.", tipo: "data", width: "100px" },
  // Bloco TBL.5: requerPermissao "preco:view" — usuarios sem permissao
  // nao veem a coluna inteira (libera espaco util na tabela).
  { key: "valor", label: "Valor", labelCurto: "valor", tipo: "moeda", width: "100px", requerPermissao: "preco:view" },
  { key: "status", label: "Status", width: "100px" },
];

// Ordem nova com a aprovação datada antes da revisão: segue para o PPCP (as
// duas etapas têm data), mas com o aviso de que a ordem não foi respeitada.
const APROVADO_ANTES = { status: "Aprovado antes da revisão", statusVariant: "warning" };

export default function LiberacaoPPCP() {
  const { dados, erro, initialLoading } = useDados();

  // Memoizado (e antes dos early-returns — Rules of Hooks): sem isso, cada
  // re-render recriava `linhas` (filter+map+sort de 400+ itens) e a nova
  // referência invalidava toda a cadeia de memos do PainelTabela.
  const linhas = useMemo(
    () =>
      dados
        // Aprovado E revisado, nas duas ordens (utils/fluxoExecutivo).
        .filter(estaNaFilaDoPpcp)
        .map((row) => {
          const dataAprovacao = row[F.APROVACAO_DO_EXECUTIVO];
          const limiteOtimizacao = dataAprovacao
            ? addDiasUteis(new Date(dataAprovacao), 5)
            : null;

          return {
            cliente: row[F.NOME_DO_CLIENTE],
            rowPlanilha: row,
            ambiente: row[F.AMBIENTE],
            entregaContrato: row[F.ENTREGA_PREVISTA],
            dataAprovacao,
            limiteOtimizacao,
            valor: row[F.VALOR_DO_AMBIENTE],
            ...(aprovadoAntesDaRevisao(row)
              ? APROVADO_ANTES
              : buildDeadlineStatus(limiteOtimizacao)),
          };
        })
        .sort((left, right) => compareDatesAsc(left.limiteOtimizacao, right.limiteOtimizacao)),
    [dados],
  );

  const totalizadores = useMemo(
    () => [
      { label: "Total de Ambientes", valor: linhas.length },
      {
        label: "Em Atraso",
        valor: linhas.filter((linha) => linha.statusVariant === "danger").length,
      },
    ],
    [linhas],
  );

  if (initialLoading) {
    return <PageLoader scope="ppcp" />;
  }

  if (erro) {
    return <EmptyStateErro erro={erro} />;
  }

  return (
    <PainelTabela
      titulo="Liberação PPCP"
      exportPainelId="ppcp"
      colunas={COLUNAS}
      linhas={linhas}
      totalizadores={totalizadores}
      densidade="compact"
      mostrarPaginacao={false}
      persistirNoBackend
    />
  );
}
